// src/pages/sow-analysis/AnalysisHistoryTable.jsx
//
// Analysis history list: source tabs (All/SFDC/UI Upload), a merged
// Project/Opportunity filter, search, and the DataTable itself. Originally
// split out of Sowanalysisviewerpage.jsx, which used to keep an
// always-visible "Filter by Opportunity" search+list panel permanently
// eating the left half of the screen -- folded into a single toolbar
// dropdown here.
//
// One dropdown, not two: a Project and its linked Opportunity (see
// EngagementAggregate.opportunity_id) mean almost the same filter, so
// they're grouped into a single control -- Projects first (showing their
// linked Opportunity's name when they have one), then any Opportunity not
// already reachable through a Project (an Opportunity with no Project
// yet, or a Project created with no Opportunity in view, are both still
// filterable this way).

import { useMemo } from "react";
import PropTypes from "prop-types";
import { Eye, FileSearch, Plus } from "lucide-react";
import { Button, DataTable, EmptyState, Input, ScoreChip, Select, SkeletonTable, SurfaceCard, Tabs } from "../../shared/ui";

const SOURCE_TABS = [
  { key: "all", label: "All" },
  { key: "sfdc", label: "SFDC" },
  { key: "ui", label: "UI Upload" },
];

// Same wording as SOURCE_TABS, so a row's badge matches the tab that lists it.
const SOURCE_LABEL = {
  SFDC: "SFDC",
  HISTORICAL_UPLOAD: "UI Upload",
  UI_TEST: "UI Test",
};

const SOURCE_BADGE = {
  SFDC: "bg-blue-100 text-blue-700",
  HISTORICAL_UPLOAD: "bg-violet-100 text-violet-700",
  UI_TEST: "bg-violet-100 text-violet-700",
};

export default function AnalysisHistoryTable({
  activeTab,
  onTabChange,
  opportunities,
  engagements,
  selectedFilterValue,
  onFilterChange,
  onOpenUpload,
  rows,
  filteredRows,
  loading,
  search,
  onSearchChange,
  onSelect,
}) {
  const engagementNameById = useMemo(
    () => new Map(engagements.map((e) => [e.engagement_id, e.client_name])),
    [engagements]
  );

  const opportunityById = useMemo(
    () => new Map(opportunities.map((o) => [String(o.id), o])),
    [opportunities]
  );

  // One merged control: Projects first (with their linked Opportunity's
  // name appended, if any), then any Opportunity not already reachable
  // through one of those Projects -- see useSowAnalysisPage.js's own
  // "project:<id>" / "opportunity:<id>" encoding.
  const filterOptions = useMemo(() => {
    const linkedOpportunityIds = new Set(
      engagements.filter((e) => e.opportunity_id != null).map((e) => String(e.opportunity_id))
    );

    const groups = [];

    if (engagements.length) {
      groups.push({
        label: "Projects",
        options: engagements.map((e) => {
          const linkedOpp = e.opportunity_id != null ? opportunityById.get(String(e.opportunity_id)) : null;
          return {
            value: `project:${e.engagement_id}`,
            label: linkedOpp ? `${e.client_name} — ${linkedOpp.opportunity_name}` : e.client_name,
          };
        }),
      });
    }

    const unlinkedOpportunities = opportunities.filter((o) => !linkedOpportunityIds.has(String(o.id)));
    if (unlinkedOpportunities.length) {
      groups.push({
        label: "Opportunities",
        options: unlinkedOpportunities.map((o) => ({
          value: `opportunity:${o.id}`,
          label: `${o.opportunity_name} — ${o.account_name}`,
        })),
      });
    }

    return [{ value: "", label: "All" }, ...groups];
  }, [engagements, opportunities, opportunityById]);

  const columns = useMemo(
    () => [
      { id: "opportunity_name", header: "Opportunity", accessorKey: "opportunity_name" },
      { id: "account_name", header: "Account", accessorKey: "account_name" },
      {
        id: "engagement_id",
        header: "Project",
        accessorFn: (r) => (r.engagement_id ? engagementNameById.get(r.engagement_id) || "—" : "—"),
      },
      { id: "contract_type", header: "Type", accessorKey: "contract_type", width: "110px" },
      {
        id: "sf_opportunity_id",
        header: "Opty ID",
        accessorFn: (r) => r.sf_opportunity_id || "—",
        width: "150px",
      },
      {
        id: "source",
        header: "Source",
        accessorKey: "source",
        width: "110px",
        cell: (info) => {
          const source = info.getValue();
          if (!source) return "—";
          return (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SOURCE_BADGE[source] || "bg-gray-100 text-gray-600"}`}>
              {SOURCE_LABEL[source] || source}
            </span>
          );
        },
      },
      {
        id: "score_overall",
        header: "Score",
        align: "center",
        width: "90px",
        accessorKey: "score_overall",
        cell: (info) => <ScoreChip score={info.getValue()} />,
      },
      {
        id: "actions",
        header: "Actions",
        sortAllowed: false,
        align: "right",
        cell: (info) => (
          <Button
            size="sm"
            variant="secondary"
            className="!px-2"
            title="View analysis"
            aria-label="View analysis"
            onClick={() => onSelect(info.row.original.id, info.row.original)}
          >
            <Eye size={14} />
          </Button>
        ),
      },
    ],
    [engagementNameById, onSelect]
  );

  return (
    <SurfaceCard padding="p-0" className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 flex-wrap px-6 py-4 border-b border-gray-100">
        <Tabs tabs={SOURCE_TABS} active={activeTab} onChange={onTabChange} />
        <div className="flex items-center gap-2">
          {activeTab === "ui" && (
            <Button onClick={onOpenUpload}>
              <Plus size={15} /> Analyze SOWs
            </Button>
          )}
          <div className="w-72">
            <Select options={filterOptions} value={selectedFilterValue} onChange={(e) => onFilterChange(e.target.value)} />
          </div>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <SkeletonTable rows={5} columns={8} />
      ) : rows.length === 0 ? (
        <EmptyState icon={FileSearch} title="No analysis records found" description="Try a different tab or filter." />
      ) : (
        <DataTable
          title="Analysis History"
          table={{ columns, rows: filteredRows }}
          entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
          emptyMessage="No matches found."
          toolbarContent={
            <div className="w-64">
              <Input
                type="search"
                placeholder="Search by opportunity, account, opty ID…"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          }
        />
      )}
    </SurfaceCard>
  );
}

AnalysisHistoryTable.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  opportunities: PropTypes.array.isRequired,
  engagements: PropTypes.array.isRequired,
  selectedFilterValue: PropTypes.string.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onOpenUpload: PropTypes.func.isRequired,
  rows: PropTypes.array.isRequired,
  filteredRows: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};
